<template>
  <Teleport to="body">
    <div v-if="modelValue" class="gm-overlay" :class="{ 'gm-overlay--centered': isCompact }" @click.self="close">
      <button type="button" class="gm-close" aria-label="Close gallery" @click="close">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      <div v-if="loading" class="gm-loading">
        <div class="gm-spinner"></div>
      </div>

      <div v-else class="gm-grid">
        <button
          v-for="(photo, i) in photos"
          :key="i"
          type="button"
          class="gm-thumb"
          @click="activeIndex = i"
        >
          <img :src="photoUrl(photo)" :alt="`gallery-${i}`" loading="lazy" />
        </button>
      </div>

      <div v-if="!loading && detailUrl" class="gm-detail-wrap">
        <div class="gm-detail-inner">
          <NuxtLink :to="detailUrl" class="gm-detail-link">
            <span>{{ $t('detail') }}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </NuxtLink>
        </div>
      </div>

      <div v-if="activeIndex !== null" class="gm-lightbox" @click.self="activeIndex = null">
        <button type="button" class="gm-back" aria-label="Back to grid" @click="activeIndex = null">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>
        <button type="button" class="gm-close gm-close--light" aria-label="Close gallery" @click="close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <button
          v-if="photos.length > 1"
          type="button"
          class="gm-nav gm-nav--prev"
          aria-label="Previous photo"
          @click="prev"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <img class="gm-lightbox-img" :src="photoUrl(photos[activeIndex])" :alt="`gallery-${activeIndex}`" />
        <button
          v-if="photos.length > 1"
          type="button"
          class="gm-nav gm-nav--next"
          aria-label="Next photo"
          @click="next"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "GalleryModal",
  props: {
    modelValue: { type: Boolean, default: false },
    photos: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    detailUrl: { type: String, default: null },
  },
  emits: ["update:modelValue"],
  data() {
    return { activeIndex: null };
  },
  computed: {
    isCompact() {
      return this.photos.length > 0 && this.photos.length <= 12;
    },
  },
  watch: {
    modelValue(val) {
      if (!val) this.activeIndex = null;
      this.toggleBodyScroll(val);
    },
  },
  beforeUnmount() {
    if (this.modelValue) this.toggleBodyScroll(false);
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
    // Plain overflow:hidden instead of the position:fixed + negative-top +
    // scrollTo trick this used to use: that technique moves window.scrollY
    // to 0 while "locked" and then jumps back to the remembered position on
    // close — visible as a scroll-down flash right after closing on some
    // browsers. overflow:hidden never touches the page's real scroll
    // position at all, so there's nothing to jump back to.
    toggleBodyScroll(lock) {
      document.body.style.overflow = lock ? "hidden" : "";
    },
    photoUrl(photo) {
      return typeof photo === "string" ? photo : photo?.image;
    },
    prev() {
      this.activeIndex = (this.activeIndex - 1 + this.photos.length) % this.photos.length;
    },
    next() {
      this.activeIndex = (this.activeIndex + 1) % this.photos.length;
    },
  },
};
</script>

<style scoped>
.gm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(4, 35, 51, 0.94);
  z-index: 1000;
  overflow-y: auto;
  padding: 28px 24px 40px;
  user-select: none;
  -webkit-user-select: none;
}
.gm-overlay--centered {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.gm-overlay--centered .gm-grid {
  width: 100%;
  margin: 0;
}

.gm-close {
  position: fixed;
  top: 20px;
  right: 24px;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  z-index: 1002;
  padding: 8px;
  display: flex;
}

.gm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
}
.gm-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #e7bb7f;
  border-radius: 50%;
  animation: gm-spin 0.8s linear infinite;
}
@keyframes gm-spin {
  to { transform: rotate(360deg); }
}

.gm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  justify-content: center;
  gap: 14px;
  max-width: 1400px;
  margin: 0 auto;
}

.gm-detail-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1003;
  display: flex;
  justify-content: center;
  padding: 40px 24px 28px;
  background: linear-gradient(to top, rgba(4, 35, 51, 0.95) 0%, rgba(4, 35, 51, 0.45) 65%, rgba(4, 35, 51, 0) 100%);
  pointer-events: none;
}
.gm-detail-inner {
  width: 100%;
  max-width: 1400px;
  display: flex;
  justify-content: flex-end;
}
.gm-detail-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
  background: none;
  border: none;
  padding: 0;
  color: #fff;
  text-decoration: none;
  font-style: italic;
  letter-spacing: 0.02em;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 2.1rem;
  transition: gap 0.3s ease, color 0.3s ease;
}
.gm-detail-link:hover {
  gap: 14px;
  color: #e7bb7f;
}

.gm-thumb {
  position: relative;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 0;
}
.gm-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}
.gm-thumb:hover img {
  transform: scale(1.06);
}

.gm-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(4, 35, 51, 0.98);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.gm-lightbox-img {
  max-width: 100%;
  max-height: 92vh;
  object-fit: contain;
}
.gm-close--light {
  top: 20px;
  right: 24px;
}
.gm-back {
  position: fixed;
  top: 20px;
  left: 24px;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  z-index: 1002;
  padding: 8px;
  display: flex;
}
.gm-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  z-index: 1002;
  transition: background 0.2s ease;
}
.gm-nav:hover {
  background: rgba(255, 255, 255, 0.2);
}
.gm-nav--prev { left: 16px; }
.gm-nav--next { right: 16px; }

@media (max-width: 768px) {
  .gm-overlay {
    padding: 22px 12px 24px;
  }
  .gm-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .gm-detail-inner {
    justify-content: center;
  }
  .gm-nav {
    padding: 8px;
  }
}
</style>
